import {
  ApplicantCompanyLocationSelectableEntityResponseDto,
  ApplicantListPaginated,
  ApplicantListPaginatedResponseDtoWithSelectableEntities,
  ApplicantListResponseDto,
  ApplicantStatusSelectableEntityResponseDto,
  ApplicantTab,
  ApplicantTabCounts,
  JobOfferSelectableEntityResponseDto,
} from "@/lib/interfaces/applicant";
import { Button as ChakraButton, Flex, Spinner, Text, Tooltip } from "@chakra-ui/react";
import { EyeOpenIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar, ChevronDown, ChevronDownIcon, InfoIcon, MoreHorizontal, Phone, Sparkles, Trash2Icon, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { cn } from "../../lib/utils/cn";
import { getInitials } from "../../lib/utils/initials";
import { Button } from "../../ui/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/components/ui/dropdown-menu";
import SearchInput from "../../ui/components/ui/search-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/components/ui/table";
import GutIcon from "../../ui/icons/GutIcon";
import NeutralIcon from "../../ui/icons/NeutralIcon";
import SchlechtIcon from "../../ui/icons/SchlechtIcon";
import AppStatus from "./AppStatus";

export enum Evaluation {
  Gut = "GOOD",
  Schlecht = "BAD",
  Neutral = "NEUTRAL",
}

const evaluationIcons = {
  [Evaluation.Gut]: GutIcon,
  [Evaluation.Schlecht]: SchlechtIcon,
  [Evaluation.Neutral]: NeutralIcon,
};

const columnNames: { [key: string]: string } = {
  name: "Bewerber",
  jobOfferTitle: "Beworben als",
  companyLocationTitle: "Standort",
  appointment: "Termin",
  phone: "Telefon",
  valuation: "Bewertung",
  belongsToStatus: "Status",
};

// Tabs only appear when the org is AI-enabled. Counts come from the response `tabCounts`.
const AI_TABS: { key: ApplicantTab; label: string }[] = [
  { key: "withAppointment", label: "Mit Termin" },
  { key: "withoutAppointment", label: "Ohne Termin" },
  { key: "noResponse", label: "Keine Reaktion" },
  { key: "direct", label: "Direktkandidaten" },
];

// WhatsApp-AI status → German label (shown as a badge, mainly outside "Mit Termin").
const AI_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: "Terminiert",
  APPOINTMENT_CANCELLED: "Termin abgesagt",
  IN_CONVERSATION: "Im Gespräch",
  NO_RESPONSE: "Keine Reaktion",
};

// AI appointment `startsAt` is UTC → render in the viewer's local timezone.
const formatListAppointment = (value?: string | null) => {
  if (!value) return undefined;
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : format(date, "EE, dd.MM.yyyy HH:mm", { locale: de });
};

function DataTable() {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();
  const { user } = useUser();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [isAnonymizeDialogOpen, setIsAnonymizeDialogOpen] = useState<{ isOpen: boolean; applicant: ApplicantListResponseDto }>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<{ isOpen: boolean; applicant: ApplicantListResponseDto }>();

  const [nameFilter, setNameFilter] = useState<"ASC" | "DESC">();
  const [statusSortFilter, setStatusSortFilter] = useState<"ASC" | "DESC">();
  // Ohne gesetzten Wunsch sortiert das Backend den Termin absteigend — der Pfeil zeigt das an.
  const [appointmentSortFilter, setAppointmentSortFilter] = useState<"ASC" | "DESC">();
  const [isLoading, setIsLoading] = useState(false);

  const [data, setData] = useState<ApplicantListResponseDto[]>([]);
  const [jobOffers, setJobOffers] = useState<JobOfferSelectableEntityResponseDto[]>([]);
  const [status, setStatus] = useState<ApplicantStatusSelectableEntityResponseDto[]>([]);
  const [companyLocations, setCompanyLocations] = useState<ApplicantCompanyLocationSelectableEntityResponseDto[]>([]);
  const [selectedCompanyLocation, setSelectedCompanyLocation] = useState<string | undefined>(undefined);

  const [showAiTabs, setShowAiTabs] = useState(false);
  const [showMigrationNotice, setShowMigrationNotice] = useState(false);
  const [activeTab, setActiveTab] = useState<ApplicantTab>("withAppointment");
  const [tabCounts, setTabCounts] = useState<ApplicantTabCounts>({
    withAppointment: 0,
    withoutAppointment: 0,
    noResponse: 0,
    direct: 0,
  });

  const [pageCount, setPageCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filter, setFilter] = useState("");
  const requestId = useRef(0);

  const handleDeleteClick = async (id: string, action: "anonymize" | "delete") => {
    await apiRequest(`applicants/${id}/${action === "anonymize" ? "anonymize" : ""}`, "DELETE");
    if (action === "delete") {
      setIsDeleteDialogOpen(undefined);
    } else {
      setIsAnonymizeDialogOpen(undefined);
    }
    getApplicants();
  };

  const columns: ColumnDef<ApplicantListResponseDto>[] = [
    {
      accessorKey: "name",
      header: () => (
        <Button
          variant="ghost"
          onClick={() => {
            setNameFilter(nameFilter === "ASC" ? "DESC" : "ASC");
            setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
            setStatusSortFilter(undefined);
            setAppointmentSortFilter(undefined);
          }}
        >
          {columnNames.name}
          <ChevronDownIcon className={cn("h-4 w-4", nameFilter === "ASC" ? "rotate-180" : "")} />
        </Button>
      ),
      cell: ({ row }) => {
        const applicant = row.original;
        const badges: string[] = [];
        if (applicant.isAiWhatsAppApplicant && activeTab !== "withAppointment" && applicant.aiWhatsAppStatus) {
          badges.push(AI_STATUS_LABELS[applicant.aiWhatsAppStatus] ?? applicant.aiWhatsAppStatus);
        }
        if (applicant.isAiWhatsAppApplicant && activeTab === "withoutAppointment" && !applicant.isHandedOver) {
          badges.push("Validierung läuft");
        }
        if (applicant.optOut) badges.push("Abgemeldet");

        return (
          <div className="flex items-center gap-3">
            <span
              className="grid h-9 w-9 flex-none place-items-center rounded-full text-xs font-bold text-white"
              style={{ background: "linear-gradient(135deg,#4a52c9,#5a3ec8)" }}
            >
              {getInitials(applicant.name)}
            </span>
            <div>
              <div className="font-bold text-slate-900">{applicant.name}</div>
              {badges.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {badges.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "jobOfferTitle",
      header: () => <Text>{columnNames.jobOfferTitle}</Text>,
      cell: ({ row }) => row.getValue("jobOfferTitle") || " - ",
    },
    {
      accessorKey: "companyLocationTitle",
      header: () => <Text>{columnNames.companyLocationTitle}</Text>,
      cell: ({ row }) => row.getValue("companyLocationTitle") || " - ",
    },
    // Termin: AI tabs only; value rendered only in "Mit Termin".
    ...(activeTab !== "direct"
      ? [
          {
            id: "appointment",
            header: () => (
              <Button
                variant="ghost"
                onClick={() => {
                  setAppointmentSortFilter(appointmentSortFilter === "ASC" ? "DESC" : "ASC");
                  setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
                  setNameFilter(undefined);
                  setStatusSortFilter(undefined);
                }}
              >
                {columnNames.appointment}
                <ChevronDownIcon className={cn("h-4 w-4", appointmentSortFilter === "ASC" ? "rotate-180" : "")} />
              </Button>
            ),
            cell: ({ row }: { row: { original: ApplicantListResponseDto } }) => {
              const appointment = row.original.aiWhatsAppAppointment;
              const value =
                activeTab === "withAppointment" && appointment?.status === "booked"
                  ? formatListAppointment(appointment.startsAt)
                  : undefined;
              return value ? (
                <span className="inline-flex items-center gap-2 whitespace-nowrap font-medium text-slate-800">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  {value}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              );
            },
          } as ColumnDef<ApplicantListResponseDto>,
        ]
      : []),
    {
      accessorKey: "phone",
      header: () => <Text>{columnNames.phone}</Text>,
      cell: ({ row }) => {
        const phone = row.original.phone;
        if (!phone) return <span className="text-slate-400">—</span>;
        return (
          <a
            href={`tel:${phone}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 whitespace-nowrap text-slate-700 transition-colors hover:text-primary"
          >
            <Phone className="h-4 w-4 text-slate-400" />
            {phone}
          </a>
        );
      },
    },
    {
      accessorKey: "valuation",
      header: () => <Text>{columnNames.valuation}</Text>,
      cell: ({ row }) => {
        const evaluationValue = row.getValue("valuation") as Evaluation;
        const IconComponent = evaluationIcons[evaluationValue];
        return <div className="flex">{IconComponent ? <IconComponent selected={true} /> : null}</div>;
      },
    },
    {
      accessorKey: "belongsToStatus",
      header: () => (
        <Button
          variant="ghost"
          onClick={() => {
            setStatusSortFilter(statusSortFilter === "ASC" ? "DESC" : "ASC");
            setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
            setNameFilter(undefined);
            setAppointmentSortFilter(undefined);
          }}
        >
          {columnNames.belongsToStatus}
          <ChevronDownIcon className={cn("h-4 w-4", statusSortFilter === "ASC" ? "rotate-180" : "")} />
        </Button>
      ),
      cell: ({ row }) => {
        const statusEntity = status.find((entry) => entry.id === row.getValue("belongsToStatus"));
        return (
          <div className="flex items-center gap-2">
            <AppStatus status={statusEntity ? statusEntity.value : ""} color={statusEntity ? statusEntity.color : ""} />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Details",
      enableHiding: false,
      cell: ({ row }) => {
        const applicant = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <span className="sr-only">Menü öffnen</span>
                <MoreHorizontal className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem className="flex gap-2 cursor-pointer">
                  <Link to={`/bewerber/${applicant._id}`} className="flex gap-2">
                    <InfoIcon className="h-4 w-4 text-primary self-center" />
                    Details
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex gap-2 cursor-pointer"
                  disabled={user?.permissionApplicants !== "FULL" && !user?.permissionOrganizationAdmin}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAnonymizeDialogOpen({ isOpen: true, applicant });
                  }}
                >
                  <EyeOpenIcon className="h-4 w-4 text-primary" />
                  Anonymisieren
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={user?.permissionApplicants !== "FULL" && !user?.permissionOrganizationAdmin}
                  className="flex gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen({ isOpen: true, applicant });
                  }}
                >
                  <Trash2Icon className="h-4 w-4 text-error" />
                  Löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { sorting, pagination, columnFilters, columnVisibility, rowSelection },
  });

  const getInitial = useCallback(async () => {
    setIsLoading(true);
    let url = "applicants/initial";
    if (selectedCompanyLocation) {
      url += `/companyLocation/${selectedCompanyLocation}`;
    }
    url += `?limit=${pagination.pageSize}&page=1`;
    const response = await apiRequest<ApplicantListPaginatedResponseDtoWithSelectableEntities>(url, "GET");

    if (response.data) {
      setData(response.data.applicants.docs);
      setPageCount(response.data.applicants.pageCount);
      setItemCount(response.data.applicants.count);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setJobOffers(response.data.jobOffers);
      setStatus(response.data.status);
      setCompanyLocations(response.data.companyLocations);
      setShowAiTabs(response.data.showAiTabs);
      setTabCounts(response.data.applicants.tabCounts);
      setActiveTab(response.data.showAiTabs ? "withAppointment" : "direct");
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, selectedCompanyLocation]);

  const getApplicants = useCallback(async () => {
    const reqId = ++requestId.current;
    setIsLoading(true);
    let url = `applicants?tab=${activeTab}&limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
    if (filter) url += `&${filter}`;
    if (nameFilter) url += `&nameFilter=${nameFilter}`;
    if (statusSortFilter) url += `&applicantStatusFilter=${statusSortFilter}`;
    if (appointmentSortFilter) url += `&appointmentDateFilter=${appointmentSortFilter}`;

    const response = await apiRequest<ApplicantListPaginated>(url, "GET");
    // Ignore stale responses so a slow request can't overwrite a newer tab/filter.
    if (reqId !== requestId.current) return;

    if (response.data) {
      setData(response.data.docs);
      setPageCount(response.data.pageCount);
      setItemCount(response.data.count);
      setTabCounts(response.data.tabCounts);
    }
    setIsLoading(false);
  }, [apiRequest, activeTab, filter, nameFilter, statusSortFilter, appointmentSortFilter, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    getInitial();
  }, [getInitial]);

  useEffect(() => {
    if (status.length) {
      getApplicants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getApplicants, activeTab, pagination.pageIndex, pagination.pageSize, filter, nameFilter, statusSortFilter, appointmentSortFilter]);

  const handleTabChange = (tab: ApplicantTab) => {
    setActiveTab(tab);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  // Einmaliger Hinweis nach der KI-Umstellung: bleibt sichtbar, bis der User ihn wegklickt.
  useEffect(() => {
    if (showAiTabs && localStorage.getItem("applicantTabsMigrationNoticeSeen") !== "true") {
      setShowMigrationNotice(true);
    }
  }, [showAiTabs]);

  const dismissMigrationNotice = () => {
    localStorage.setItem("applicantTabsMigrationNoticeSeen", "true");
    setShowMigrationNotice(false);
  };

  return (
    <div className="w-full">
      {showMigrationNotice && (
        <div className="mb-4 flex items-start gap-4 rounded-xl border border-[#dbe3f6] bg-gradient-to-r from-[#eef1ff] via-[#e7f1fd] to-[#e4f5fc] px-5 py-4">
          <span className="mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-lg bg-[#5257d6] text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <div className="font-bold text-slate-900">
              Neu: Bewerber aus der WhatsApp-KI erscheinen jetzt in eigenen Reitern
            </div>
            <div className="mt-0.5 text-sm text-slate-600">
              Deine bestehenden Bewerber findest du unter „Direktkandidaten"
              {tabCounts.direct ? ` (${tabCounts.direct} Bewerber)` : ""}.
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            <Button size="sm" onClick={dismissMigrationNotice}>
              Verstanden
            </Button>
            <button
              type="button"
              onClick={dismissMigrationNotice}
              aria-label="Hinweis schließen"
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <Flex className="items-center py-4 gap-4">
        <Dialog
          open={isAnonymizeDialogOpen?.isOpen || isDeleteDialogOpen?.isOpen}
          onOpenChange={
            isAnonymizeDialogOpen?.isOpen
              ? (value) =>
                  setIsAnonymizeDialogOpen(
                    value === false ? undefined : { isOpen: true, applicant: isAnonymizeDialogOpen.applicant },
                  )
              : (value) =>
                  setIsDeleteDialogOpen(
                    value === false
                      ? undefined
                      : isDeleteDialogOpen?.applicant
                        ? { isOpen: true, applicant: isDeleteDialogOpen.applicant }
                        : undefined,
                  )
          }
        >
          {(isAnonymizeDialogOpen || isDeleteDialogOpen) && (
            <>
              {isAnonymizeDialogOpen?.applicant && (
                <ConfirmDeleteDialog
                  applicant={isAnonymizeDialogOpen.applicant}
                  isAnonymizeDialogOpen={isAnonymizeDialogOpen.isOpen}
                  handleDeleteClick={handleDeleteClick}
                />
              )}
              {isDeleteDialogOpen?.applicant && (
                <ConfirmDeleteDialog
                  applicant={isDeleteDialogOpen.applicant}
                  isAnonymizeDialogOpen={isAnonymizeDialogOpen?.isOpen ?? false}
                  handleDeleteClick={handleDeleteClick}
                />
              )}
            </>
          )}
        </Dialog>
        <Flex className="justify-between w-full items-center">
          <Filter
            jobOffers={jobOffers}
            status={status}
            companyLocations={companyLocations}
            selectedCompanyLocation={selectedCompanyLocation}
            setSelectedCompanyLocation={setSelectedCompanyLocation}
            setFilter={setFilter}
            setPagination={setPagination}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Spalten <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {columnNames[column.id.toString()]}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Flex>
      </Flex>

      {/* Reiter — nur wenn die Organisation KI-aktiviert ist */}
      {showAiTabs && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {AI_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={cn(
                "rounded-lg border px-3.5 py-2 text-sm font-semibold transition",
                activeTab === tab.key
                  ? "border-[#d9e2f2] bg-[#eef2fb] text-[#0b6f95]"
                  : tab.key === "direct"
                    ? // Anker nach der Umstellung: bestehende (Nicht-KI-)Bewerber liegen hier.
                      "border-transparent font-bold text-primary hover:bg-white"
                    : "border-transparent text-slate-500 hover:bg-white hover:text-slate-700",
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <span>
                  {tab.label}{" "}
                  <span
                    className={cn(
                      "font-semibold",
                      activeTab === tab.key
                        ? "text-primary"
                        : tab.key === "direct"
                          ? "text-primary/70"
                          : "text-slate-400",
                    )}
                  >
                    ({tabCounts[tab.key]})
                  </span>
                </span>
                {tab.key === "direct" && (
                  <Tooltip
                    hasArrow
                    placement="top"
                    label="Bewerber bis zum 24. August 2026 und Bewerber ohne WhatsApp KI sind hier abgelegt."
                  >
                    <span className="inline-flex cursor-help text-slate-400">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      {showAiTabs && activeTab === "withAppointment" && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-[#dbe3f6] bg-gradient-to-r from-[#eef1ff] via-[#e7f1fd] to-[#e4f5fc] px-4 py-3 text-sm text-[#33406b]">
          <Sparkles className="h-4 w-4 flex-none text-[#5257d6]" />
          Priorisiert: Bewerber mit fest vereinbartem telefonischen Kennenlerntermin — hier zuerst bearbeiten.
        </div>
      )}

      <div className="rounded-md border overflow-hidden">
        {isLoading ? (
          <Flex className="justify-center w-full h-36 items-center bg-white">
            <Spinner size="xl" color="primary.500" />
          </Flex>
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-white">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  // AI leads still being validated (not handed over yet) are de-emphasised.
                  const muted =
                    row.original.isAiWhatsAppApplicant &&
                    activeTab === "withoutAppointment" &&
                    !row.original.isHandedOver;
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={cn("hover:bg-slate-100 cursor-pointer", muted && "bg-slate-50 opacity-60")}
                      onClick={() => navigate(`/bewerber/${row.original._id}`)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Keine Resultate.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex items-center justify-between py-4">
        <Text className="text-sm text-gray-500">
          * Aus datenschutzrechtlichen Gründen werden Bewerberdaten 6 Monate nach Eintragung anonymisiert.
        </Text>
        <div className="flex flex-col gap-2">
          <div className="text-sm text-end">{itemCount} Ergebnisse gesamt</div>
          <Flex gap="6">
            <div className="flex space-x-2 items-center">
              <Text fontSize="medium">
                Seite {pagination.pageIndex + 1} von {pageCount}
              </Text>
              <Button
                variant="outline"
                size="sm"
                className="text-base"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="text-base"
              >
                weiter
              </Button>
            </div>
            <Flex alignItems="center">
              <Text fontSize="medium" className="text-base">
                Zeigen
              </Text>
              <select
                className="py-1 pr-7 pl-3 ml-2 rounded-sm border-gray-200 text-sm"
                value={pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
              >
                {[5, 10, 15, 20].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </Flex>
          </Flex>
        </div>
      </div>
    </div>
  );
}

const Filter = ({
  jobOffers,
  status,
  companyLocations,
  setSelectedCompanyLocation,
  selectedCompanyLocation,
  setFilter,
  setPagination,
}: {
  jobOffers: JobOfferSelectableEntityResponseDto[];
  status: ApplicantStatusSelectableEntityResponseDto[];
  companyLocations: ApplicantCompanyLocationSelectableEntityResponseDto[];
  setSelectedCompanyLocation: (companyLocation: string | undefined) => void;
  selectedCompanyLocation: string | undefined;
  setFilter: (filter: string) => void;
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => void;
}) => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobOfferFilter, setJobOfferFilter] = useState("all");

  useEffect(() => {
    let queryString = "";
    if (searchText) {
      queryString += `search=${searchText}&`;
      setStatusFilter("all");
      setJobOfferFilter("all");
    } else {
      if (statusFilter !== "all") {
        queryString += `belongsToStatus=${statusFilter}&`;
      }
      if (jobOfferFilter !== "all") {
        queryString += `belongsToJobOfferId=${jobOfferFilter}&`;
      }
    }
    queryString = queryString.slice(0, -1);
    setFilter(queryString);
    setPagination({ pageIndex: 0, pageSize: 10 });
  }, [searchText, statusFilter, jobOfferFilter, setFilter, setPagination]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value) {
      setStatusFilter("all");
      setJobOfferFilter("all");
    }
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setSearchText("");
  };

  const handleJobOfferChange = (value: string) => {
    setJobOfferFilter(value);
    setSearchText("");
  };

  const handleCompanyLocationChange = (value: string) => {
    setSelectedCompanyLocation(value === "all" ? undefined : value);
    setSearchText("");
  };

  return (
    <Flex className="items-center py-4 gap-4">
      <SearchInput setSearchText={handleSearchChange} searchText={searchText} />
      <Select onValueChange={handleStatusChange} value={statusFilter}>
        <SelectTrigger>
          <SelectValue placeholder="Wählen Sie ein Status aus" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle Status</SelectItem>
          {status.map((entry) => (
            <SelectItem key={entry.id} value={entry.id}>
              {entry.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={handleCompanyLocationChange} value={selectedCompanyLocation ? selectedCompanyLocation : "all"}>
        <SelectTrigger>
          <SelectValue placeholder="Wählen Sie einen Standort aus" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle Standorte</SelectItem>
          {companyLocations?.map((companyLocation) => (
            <SelectItem key={companyLocation._id} value={companyLocation._id}>
              {companyLocation.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select onValueChange={handleJobOfferChange} value={jobOfferFilter}>
        <SelectTrigger>
          <SelectValue placeholder="Wählen Sie eine Stelle aus" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle Stellen</SelectItem>
          {jobOffers.map((jobOffer) => (
            <SelectItem key={jobOffer._id} value={jobOffer._id}>
              {jobOffer.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Flex>
  );
};

const ConfirmDeleteDialog = ({
  applicant,
  isAnonymizeDialogOpen,
  handleDeleteClick,
}: {
  applicant: ApplicantListResponseDto;
  isAnonymizeDialogOpen: boolean;
  handleDeleteClick: (id: string, action: "anonymize" | "delete") => void;
}) => {
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          Bewerber {applicant.name} {isAnonymizeDialogOpen ? "anonymisieren" : "löschen"}
        </DialogTitle>
        <DialogDescription>
          {isAnonymizeDialogOpen
            ? "Sind Sie sich sicher, dass Sie den Bewerber anonymisieren wollen? Diese Aktion kann nicht rückgängig gemacht werden. Er wird immer noch in der Statistik angezeigt"
            : "Sind Sie sich sicher, dass Sie den Bewerber löschen wollen? Diese Aktion kann nicht rückgängig gemacht werden. Er wird auch aus der Statistik gelöscht"}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild>
          <ChakraButton
            colorScheme="primary"
            className="flex gap-2 p-2 text-white"
            justifyContent="flex-start"
            variant="outline"
          >
            Schliessen
          </ChakraButton>
        </DialogClose>
        <DialogClose asChild>
          <ChakraButton
            borderColor="error"
            textColor="error"
            className="flex gap-2 p-2"
            justifyContent="flex-start"
            variant="outline"
            onClick={() => handleDeleteClick(applicant._id, isAnonymizeDialogOpen ? "anonymize" : "delete")}
          >
            {isAnonymizeDialogOpen ? "Anonymisieren" : "Löschen"}
          </ChakraButton>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

export { DataTable };
